/* ====================================================================
 *  BPS VE ENGINE  —  calculation core (no UI, no external data)
 *
 *  Volumetric efficiency here uses the Holley convention:
 *
 *      VE = (fresh air mass actually trapped per cycle)
 *           / (displacement x air density AT THE MANIFOLD)
 *
 *  Because the reference density is taken at manifold conditions,
 *  raising boost raises AIR MASS but does NOT by itself raise VE.
 *  Boost only moves VE through real breathing effects: exhaust
 *  backpressure, scavenging, and residual fraction.
 *
 *  Model form: a physically-referenced base efficiency multiplied by
 *  named, independently-computed factors. Every empirical coefficient
 *  is named in COEFF below with its engineering basis.
 * ==================================================================== */

/* ---------------------------------------------------------------- *
 *  Documented empirical coefficients.
 *  These exist because the model is quasi-steady: it does not solve
 *  unsteady gas dynamics, so a few effects are represented by
 *  calibrated relationships from published engine test practice.
 * ---------------------------------------------------------------- */
const COEFF = {
  MACH_INDEX_REFERENCE: 1.10,
  // Inlet Mach index Z (Livengood & Eichelberger). Published Z-vs-VE
  // data is flat to about Z=0.4, is clearly rolling off by the classic
  // Z=0.6 knee, and collapses past Z=1. This reference value places a
  // continuous curve through those three observations, so valve size
  // and port area influence VE smoothly instead of via a hard switch.

  MACH_INDEX_FALLOFF: 4.27,
  // Curvature of that rolloff. Together these two place the curve at
  // ~0.99 at Z=0.4, ~0.93 at the classic Z=0.6 knee, ~0.85 at Z=0.75
  // and ~0.60 at Z=1.0, matching the shape of published Z-vs-VE data.

  VALVE_CD_MEAN: 0.36,
  // Cycle-mean discharge coefficient of a poppet valve over a full
  // opening event. Peak Cd is ~0.6 at high lift but the mean across
  // opening and closing ramps is far lower. Standard Mach-index value.

  HELMHOLTZ_ORDER: 3.0,
  // Which resonance order lands in the powerband. The intake valve is
  // open roughly a third of the 720-degree cycle, so third-order
  // intake resonance is the one that typically aligns with peak
  // torque on a port-injected engine.

  RAM_TUNING_AMPLITUDE: 0.07,
  // Peak-to-mean VE swing from intake resonance on a port-injected
  // engine with individual runners: about +/-7%. Log-normal width
  // below reflects how broad that resonance is in practice.

  RAM_TUNING_WIDTH: 0.42,
  // Std dev in ln(rpm) of the resonance envelope. ~0.42 gives a usable
  // band roughly 0.65x to 1.5x the tuned speed.

  INERTIA_RECOVERY_ORDER: 2.0,
  // Charge inertia recovers late-IVC backflow as a function of port
  // velocity. Momentum scales with velocity squared, hence order 2.

  INERTIA_RECOVERY_Z: 0.34,
  // Inlet Mach index at which charge momentum recovers half of the
  // late-IVC backflow. Set so recovery is largely saturated by the
  // top of a typical powerband. A long-duration cam does not recover
  // its late-IVC backflow until port velocity is genuinely high, which
  // is why big cams breathe poorly well past idle. Z is already normalised port velocity, so
  // tying recovery to it links the cam and port models physically
  // instead of introducing a second, independent rpm reference.

  CAM_RAM_PACKING: 1.85,
  // A late intake valve closing is a penalty at low speed but a
  // benefit at high speed: once port momentum is high, charge keeps
  // entering after BDC and packs the cylinder past its swept volume.
  // This is the asymptotic ceiling on effective volume at IVC relative
  // to volume at BDC. 1.85 applied to the un-trapped fraction gives a
  // peak effective filling of roughly 1.15-1.20x swept volume on a
  // long-duration individual-runner engine, which is the range
  // published ram-charging measurements support.

  INTAKE_STROKE_DEGREES: 180,
  // The intake stroke itself. Valve open duration beyond this adds
  // time-area per cycle, which is why a longer cam relieves the
  // high-rpm inlet restriction even though it hurts trapping.

  RESIDUAL_POLYTROPIC_N: 1.30,
  // Polytropic exponent for residual gas re-expansion from exhaust
  // pressure down to manifold pressure. Standard for burned gas.

  SCAVENGE_EFFECTIVENESS: 0.55,
  // Fraction of the theoretically available overlap blow-through that
  // actually becomes trapped fresh charge rather than passing straight
  // out the exhaust. Blow-through is never fully effective.

  REVERSION_SEVERITY: 0.85,
  // Scales how strongly adverse overlap pressure ratio dilutes the
  // fresh charge. 1.0 would mean every unit of reverted mass displaces
  // an equal unit of fresh charge; some re-enters, so slightly under 1.

  OVERLAP_CD: 0.55,
  // Discharge coefficient for flow across the valves during overlap.
  // Lower than a steady-flow peak Cd because the valves are at low
  // lift and the path is short and highly unsteady.

  OVERLAP_VELOCITY_LIMIT: 0.70,
  // Fraction of local sonic velocity at which overlap flow is capped.
  // Real short-duration pulse flow does not reach full choked velocity.

  REBREATHE_FRACTION: 0.80,
  // Share of gas pushed into the intake port during overlap that is
  // actually drawn back into the SAME cylinder on the following intake
  // stroke. The remainder disperses into the plenum or is scavenged.

  OVERLAP_EGT_FRACTION: 0.72,
  // Gas crossing the valves during overlap has already blown down and
  // partly expanded, so it is cooler than peak cycle EGT. This is its
  // temperature as a fraction of the stated exhaust gas temperature.

  REVERSION_THERMAL_CAP: 3.2,
  // Gas pushed back through the intake valve during overlap is exhaust
  // at combustion temperature. Per unit MASS it occupies far more
  // VOLUME than fresh charge, so it displaces disproportionately more
  // of the cylinder. This caps that density ratio, since the reverted
  // slug cools against port walls and mixes before it is re-ingested.

  PLENUM_REFILL_EXPONENT: 0.85,
  // How strongly the manifold-emptying ratio depresses pressure at the
  // valve. Below 1 because the induction system partially refills
  // DURING the intake event, not only between events.

  MIN_PORT_PRESSURE_RATIO: 0.30,
  // Floor on port pressure relative to plenum pressure. Even a very
  // large induction volume at very low engine speed cannot pull the
  // port below a fraction of plenum pressure before flow through the
  // throttle and compressors catches up.

  EXHAUST_TEMP_FLOOR_K: 640,
  // Exhaust gas temperature at very light load. EGT is NOT constant:
  // at low load far less fuel is burned per cycle while heat loss to
  // the chamber and port walls is proportionally larger, so exhaust
  // leaves much cooler. Typical spread is roughly 600-700 K at light
  // load against 1200-1600 K at full load.

  EXHAUST_TEMP_LOAD_EXPONENT: 0.55,
  // How EGT rises with load between that floor and the stated full-load
  // value. Sub-linear: EGT climbs quickly off idle then flattens.

  TURBINE_FLOW_EXPONENT: 1.20,
  // Exhaust manifold gauge pressure rises as (mass flow)^1.2 across a
  // fixed turbine nozzle: slightly faster than linear, because the
  // nozzle chokes progressively as flow climbs. The consequence is
  // that backpressure is LOWER than boost at part flow (favourable
  // scavenging) and only exceeds boost near the engine's flow ceiling.

  DURATION_TIME_AREA_REFERENCE: 250,
  // Advertised intake duration a typical performance cam runs. Time-area
  // credit is taken relative to this and on a square-root basis, so a
  // long cam relieves inlet restriction without erasing it.

  FRICTION_PUMPING_RPM: 200,
  // Below roughly this speed, cycle-to-cycle instability and heat
  // transfer into the charge cut breathing. Deliberately small: at
  // wide-open throttle a healthy engine still breathes well at
  // cranking speeds, and the real low-speed losses are the cam and
  // residual terms, which are computed separately.

  CHARGE_HEATING_FACTOR: 0.045,
  // VE loss per unit of (residual fraction), from hot residuals and
  // port walls heating and expanding the incoming charge.
};

const IN_TO_M = 0.0254;
const CI_TO_M3 = 1.6387064e-5;
const R_AIR = 287.05;
const KPA_TO_PA = 1000;

/* ================================================================== *
 *  GEOMETRY
 * ================================================================== */

function deriveGeometry(spec) {
  const bore = spec.bore * IN_TO_M;
  const stroke = spec.stroke * IN_TO_M;
  const rod = spec.rodLength * IN_TO_M;
  const Vd = (spec.displacement * CI_TO_M3) / spec.cylinders;
  const Vc = Vd / (spec.compressionRatio - 1);
  const pistonArea = (Math.PI / 4) * bore * bore;

  return { bore, stroke, rod, Vd, Vc, pistonArea };
}

// Instantaneous cylinder volume, crank angle measured from TDC.
function cylinderVolume(thetaDeg, geo) {
  const r = geo.stroke / 2;
  const th = (thetaDeg * Math.PI) / 180;
  const s = Math.sin(th);
  const x =
    r * (1 - Math.cos(th)) + geo.rod - Math.sqrt(geo.rod * geo.rod - r * r * s * s);
  return geo.Vc + geo.pistonArea * x;
}


/* ------------------------------------------------------------------ *
 *  Valve lift profile. A cosine-power lobe fitted so that lift equals
 *  exactly 0.050" at the stated .050" duration, and equals max lift at
 *  the lobe centre. Gives a real lift value at any crank angle, which
 *  the overlap model needs.
 * ------------------------------------------------------------------ */

function lobeExponent(dur050, durTotal, maxLift) {
  const c = Math.cos((Math.PI * dur050) / (2 * durTotal));
  if (!(c > 0 && c < 1) || !(maxLift > 0.05)) return 2;
  return Math.log(0.05 / maxLift) / Math.log(c);
}

function liftAt(thetaDeg, centerDeg, durTotal, expn, maxLift) {
  let phi = thetaDeg - centerDeg;
  while (phi > 360) phi -= 720;
  while (phi < -360) phi += 720;
  if (Math.abs(phi) >= durTotal / 2) return 0;
  const c = Math.cos((Math.PI * phi) / durTotal);
  return c <= 0 ? 0 : maxLift * Math.pow(c, expn);
}

/* ================================================================== *
 *  CAM EVENTS
 *  Works from whatever the user supplied: explicit valve events if
 *  known, otherwise derived from duration and centerline.
 * ================================================================== */

function deriveCamEvents(spec) {
  const advI = spec.intakeDurationAdv || spec.intakeDuration050 + 52;
  const advE = spec.exhaustDurationAdv || spec.exhaustDuration050 + 52;

  // Advertised events define the true open window (seat to seat).
  const ivo = spec.ivo != null ? spec.ivo : advI / 2 - spec.intakeCenterline;
  const ivc = spec.ivc != null ? spec.ivc : advI / 2 + spec.intakeCenterline - 180;
  const evo = spec.evo != null ? spec.evo : advE / 2 + spec.exhaustCenterline - 180;
  const evc = spec.evc != null ? spec.evc : advE / 2 - spec.exhaustCenterline;

  const overlap = ivo + evc;
  const lsa =
    spec.lsa != null ? spec.lsa : (spec.intakeCenterline + spec.exhaustCenterline) / 2;

  return { ivo, ivc, evo, evc, overlap, lsa, advI, advE };
}

/* ================================================================== *
 *  1. BASE AIRFLOW
 *  Theoretical displacement flow. This is the denominator of VE and
 *  the reference every later factor modifies.
 * ================================================================== */

function calculateBaseAirflow(spec, geo, rpm, manifoldKpa, chargeTempK) {
  // Classic shop formula, engine total, at 100% VE.
  const theoreticalCFM = (spec.displacement * rpm) / 3456;

  const manifoldDensity = (manifoldKpa * KPA_TO_PA) / (R_AIR * chargeTempK);
  const referenceMassPerCycle = geo.Vd * manifoldDensity; // kg, one cylinder
  const intakeEventsPerSec = (rpm / 60 / 2) * spec.cylinders;
  const referenceMassFlow = referenceMassPerCycle * intakeEventsPerSec;

  return { theoreticalCFM, manifoldDensity, referenceMassPerCycle, referenceMassFlow };
}

/* ================================================================== *
 *  2. VALVE / PORT FLOW LIMIT
 *  Inlet Mach index. This is what produces high-rpm falloff, and it
 *  responds directly to valve diameter, valve count, bore and stroke.
 * ================================================================== */

function calculateValveFlowLimit(spec, geo, rpm) {
  const valveArea =
    spec.intakeValveCount *
    (Math.PI / 4) *
    Math.pow(spec.intakeValveDiameter * IN_TO_M, 2);

  // Mean piston speed, m/s
  const meanPistonSpeed = (2 * geo.stroke * rpm) / 60;

  // Speed of sound at intake charge temperature
  const a = Math.sqrt(1.4 * R_AIR * spec.chargeTempK);

  // Time-area credit: a valve open longer than the intake stroke has
  // more opportunity to flow per cycle, which lowers the effective
  // inlet restriction. Derived from advertised (seat-to-seat) duration.
  const openDuration = spec.intakeDurationAdv || spec.intakeDuration050 + 52;
  const timeAreaFactor = Math.sqrt(openDuration / COEFF.DURATION_TIME_AREA_REFERENCE);

  // Raw inlet Mach index: normalised port VELOCITY. Drives charge
  // momentum, and therefore inertia recovery in the cam model.
  const machIndexRaw =
    (geo.pistonArea * meanPistonSpeed) / (valveArea * COEFF.VALVE_CD_MEAN * a);

  // Restriction Mach index: the same velocity discounted by the extra
  // time-area a long-duration cam provides. Drives the flow limit.
  const machIndex = machIndexRaw / timeAreaFactor;

  // Continuous rolloff: ~1.00 at low Z, ~0.94 at the classic 0.6 knee,
  // collapsing past Z = 1 as the inlet valve chokes.
  const factor =
    1 / (1 + Math.pow(machIndex / COEFF.MACH_INDEX_REFERENCE, COEFF.MACH_INDEX_FALLOFF));

  // If the user measured head flow, correct the assumed valve capacity
  // by how the port actually compares to a plain valve-area estimate.
  let portQualityRatio = 1;
  if (spec.intakePortFlow > 0 && spec.flowTestPressure > 0) {
    // Convert measured flow to an effective area, then compare to the
    // geometric curtain area at the cam's max lift.
    const dp = spec.flowTestPressure * 249.0889; // in H2O -> Pa
    const rhoTest = 1.204;
    const vTest = Math.sqrt((2 * dp) / rhoTest);
    const qTest = (spec.intakePortFlow * 0.0004719); // cfm -> m3/s
    const effArea = qTest / vTest;

    const curtain =
      spec.intakeValveCount *
      Math.PI *
      spec.intakeValveDiameter *
      IN_TO_M *
      spec.intakeValveLift *
      IN_TO_M;

    portQualityRatio = effArea / (curtain * COEFF.VALVE_CD_MEAN);
    // Clamp: a real port is neither half nor double a reasonable estimate.
    portQualityRatio = Math.max(0.7, Math.min(1.35, portQualityRatio));
  }

  return {
    machIndex, machIndexRaw, factor: factor * portQualityRatio,
    portQualityRatio, valveArea, timeAreaFactor,
  };
}

/* ================================================================== *
 *  3. CAM EFFICIENCY
 *  Late intake valve closing lets charge back out at low speed and
 *  traps extra charge once port momentum builds. This is the single
 *  biggest reason a big cam kills low-rpm VE.
 * ================================================================== */

function calculateCamEfficiency(spec, geo, cam, rpm, machIndexRaw) {
  // Volume trapped at IVC compared to full swept volume at BDC.
  const vAtIVC = cylinderVolume(180 + cam.ivc, geo);
  const vAtBDC = cylinderVolume(180, geo);
  const trappingRatio = vAtIVC / vAtBDC;

  // Charge momentum recovers the backflow as port velocity rises.
  // Driven by the inlet Mach index, which IS normalised port velocity,
  // so a small port builds recovery sooner than a large one.
  const x = machIndexRaw / COEFF.INERTIA_RECOVERY_Z;
  const recovery =
    Math.pow(x, COEFF.INERTIA_RECOVERY_ORDER) /
    (1 + Math.pow(x, COEFF.INERTIA_RECOVERY_ORDER));

  // Rises from the static trapping ratio at low speed toward 1.0 as
  // inertia fully recovers the backflow. Charge packing BEYOND 1.0 is
  // handled by calculateRunnerTuning, not here, so the two do not
  // double-count the same inertia effect.
  const factor =
    trappingRatio + (1 - trappingRatio) * recovery * COEFF.CAM_RAM_PACKING;

  return { trappingRatio, recovery, factor, ivc: cam.ivc };
}

/* ================================================================== *
 *  4. RUNNER TUNING
 *  Helmholtz resonance of runner + cylinder. Responds to runner
 *  length, runner area, displacement and compression.
 * ================================================================== */

function calculateRunnerTuning(spec, geo, rpm) {
  const L = spec.runnerLength * IN_TO_M;
  const A = spec.runnerArea * IN_TO_M * IN_TO_M;
  const a = Math.sqrt(1.4 * R_AIR * spec.chargeTempK);

  // Effective volume: cylinder at mid-intake-stroke plus clearance.
  const vEff = geo.Vc + geo.Vd / 2;

  // End correction for the open end of the runner.
  const lEff = L + 0.6 * Math.sqrt(A / Math.PI);

  const fHelmholtz = (a / (2 * Math.PI)) * Math.sqrt(A / (lEff * vEff));
  const tunedRpm = (60 * fHelmholtz) / COEFF.HELMHOLTZ_ORDER;

  const lnRatio = Math.log(rpm / tunedRpm);
  const envelope = Math.exp(
    -(lnRatio * lnRatio) / (2 * COEFF.RAM_TUNING_WIDTH * COEFF.RAM_TUNING_WIDTH)
  );

  // Manifold style scales how much resonance is available. A single
  // plenum with short runners cannot ram-tune like individual runners.
  const styleScale =
    { individual: 1.0, longRunner: 0.9, dualPlane: 0.75, singlePlane: 0.6, sheetMetal: 0.85, plenum: 0.5 }[
      spec.manifoldType
    ] ?? 0.8;

  const factor = 1 + COEFF.RAM_TUNING_AMPLITUDE * styleScale * (2 * envelope - 1);

  return { fHelmholtz, tunedRpm, envelope, factor, styleScale };
}

/* ================================================================== *
 *  5. RPM EFFICIENCY
 *  Low-speed rolloff from heat transfer, poor mixture motion and
 *  cycle instability. Independent of the cam's IVC effect.
 * ================================================================== */

function calculateRPMEfficiency(spec, rpm) {
  const x = rpm / COEFF.FRICTION_PUMPING_RPM;
  const factor = x * x / (1 + x * x);
  return { factor };
}

/* ================================================================== *
 *  6. MAP EFFICIENCY
 *  What actually changes VE as manifold pressure falls is NOT the
 *  throttle itself (throttling shows up as reduced MAP, which is
 *  already the reference density). It is residual gas: at low MAP the
 *  clearance volume holds exhaust at near-atmospheric pressure, which
 *  re-expands and occupies space the fresh charge cannot use.
 * ================================================================== */

function calculateMAPEfficiency(spec, geo, cam, manifoldKpa, exhaustKpa) {
  const cr = spec.compressionRatio;

  // Residual re-expansion. Burned gas trapped in the clearance volume
  // at exhaust pressure expands polytropically down to manifold
  // pressure, displacing fresh charge.
  const expansion = Math.pow(exhaustKpa / manifoldKpa, 1 / COEFF.RESIDUAL_POLYTROPIC_N);
  const displacedFraction = (expansion - 1) / (cr - 1);

  const residualFraction = Math.max(0, Math.min(0.85, displacedFraction));
  const fillingFactor = Math.max(0.05, 1 - residualFraction);

  // Hot residual heats the incoming charge, expanding it further.
  const chargeHeating = 1 - COEFF.CHARGE_HEATING_FACTOR * residualFraction * (1 / Math.max(0.15, 1 - residualFraction));

  const factor = fillingFactor * Math.max(0.5, chargeHeating);

  return { residualFraction, fillingFactor, chargeHeating, factor };
}

/* ================================================================== *
 *  7. EXHAUST BACKPRESSURE / SCAVENGING
 *  Overlap is a two-way street. Favourable pressure ratio scavenges
 *  and can push VE past 100%. Adverse ratio reverts exhaust into the
 *  intake and destroys low-speed breathing on a big-overlap cam.
 * ================================================================== */

function calculateExhaustBackpressureCorrection(
  spec, cam, rpm, manifoldKpa, exhaustKpa, tunedRpm
) {
  const pressureRatio = manifoldKpa / exhaustKpa;

  // Header tuning: a correctly sized primary produces a low-pressure
  // wave at the valve during overlap, which assists the transfer
  // computed in calculateOverlapTransfer. Peaks near the tuned speed.
  let headerBonus = 0;
  if (spec.primaryDiameter > 0 && spec.primaryLength > 0 && spec.induction === "na") {
    const c = 1150 * 0.3048 * Math.sqrt(spec.exhaustTempK / 1000);
    const tunedExhaustRpm = (c * 60) / (4 * spec.primaryLength * IN_TO_M) / 2;
    const ln = Math.log(rpm / tunedExhaustRpm);
    headerBonus =
      0.04 * Math.exp(-(ln * ln) / (2 * 0.35 * 0.35)) * (cam.overlap / 360) * 3;
  }

  return { pressureRatio, headerBonus, factor: 1 + headerBonus };
}




/* ================================================================== *
 *  EXHAUST GAS TEMPERATURE
 *
 *  Treating EGT as a single constant across the whole table is wrong
 *  and it matters a great deal, because residual gas denies cylinder
 *  VOLUME in proportion to its temperature. Holding EGT at a full-load
 *  value while computing a 20 kPa cell makes the residual occupy
 *  roughly twice the volume it really would, and collapses VE in
 *  exactly the part-throttle region a street engine lives in.
 * ================================================================== */

function calculateExhaustTemperature(spec, rpm, manifoldKpa) {
  const maxManifold = spec.baroKpa + Math.max(0, spec.maxBoostPsi * 6.89476);
  const loadFraction = Math.max(0, Math.min(1, manifoldKpa / maxManifold));

  const floor = COEFF.EXHAUST_TEMP_FLOOR_K;
  const full = Math.max(floor + 50, spec.exhaustTempK);

  return floor + (full - floor) * Math.pow(loadFraction, COEFF.EXHAUST_TEMP_LOAD_EXPONENT);
}

/* ================================================================== *
 *  INDUCTION SYSTEM CHARGING
 *
 *  MAP is measured in the plenum, but the cylinder fills from the
 *  pressure AT THE VALVE. Each intake event pulls one cylinder's worth
 *  of charge out of the induction system, and the system has to refill
 *  through the throttle, compressors, intercooler and plumbing before
 *  the next event.
 *
 *  The governing quantity is how large one cylinder's swept volume is
 *  compared with the induction volume feeding it, versus how quickly
 *  that volume can be replenished. On a big-turbo car the volume
 *  between throttle and valve is large and the compressors contribute
 *  nothing at low speed, so the port sits meaningfully below the
 *  logged MAP. The effect fades as engine speed rises: the compressors
 *  begin doing work, and induction pressure waves start to help rather
 *  than lag.
 *
 *  IMPORTANT RESULT: this was built to explain poor LOW-rpm breathing
 *  on a big-turbo engine, and it does not. The refill time available
 *  between intake events is LONGER at low rpm, so the deficit is
 *  smallest exactly where the shortfall is largest. The genuine
 *  depletion effect grows with engine speed, is only a few percent for
 *  realistic plenum volumes (one cylinder removes ~4% of a 15 L
 *  induction system per event), and overlaps with what the inlet Mach
 *  index already accounts for.
 *
 *  It is therefore COMPUTED AND REPORTED but not applied. Kept because
 *  the port-vs-plenum pressure figure is useful to see, and because
 *  the reasoning should not have to be rediscovered later.
 * ================================================================== */

function calculateInductionCharging(spec, geo, rpm, manifoldKpa) {
  const inductionLitres =
    spec.inductionVolume > 0 ? spec.inductionVolume : estimateInductionVolume(spec, geo);
  const inductionVolume = inductionLitres / 1000; // m^3

  // Fraction of the induction volume removed by ONE intake event.
  const drawRatio = geo.Vd / Math.max(1e-9, inductionVolume);

  // Time available to refill between intake events on this cylinder.
  const secondsPerEvent = 120 / (rpm * spec.cylinders);

  // Refill capability. A throttle and compressor path passes flow in
  // proportion to its area and the pressure available; expressed as a
  // characteristic refill time for this induction volume.
  const throttleArea =
    (Math.PI / 4) * Math.pow(spec.throttleBodyDiameter * IN_TO_M, 2);
  const a = Math.sqrt(1.4 * R_AIR * spec.chargeTempK);
  const refillSeconds = inductionVolume / (throttleArea * a * 0.35);

  // Compressors reduce the deficit as they come up: at and above the
  // stated full-boost speed the induction system is being actively
  // pressurised and keeps up.
  const compressorSupport =
    spec.induction === "na"
      ? 0
      : Math.min(1, Math.pow(rpm / Math.max(1, spec.boostOnsetRpm), 2));

  const deficit =
    drawRatio *
    Math.pow(refillSeconds / secondsPerEvent, COEFF.PLENUM_REFILL_EXPONENT) *
    (1 - compressorSupport);

  const portPressureRatio = Math.max(
    COEFF.MIN_PORT_PRESSURE_RATIO,
    1 / (1 + deficit)
  );

  return {
    inductionLitres,
    drawRatio,
    refillSeconds,
    secondsPerEvent,
    compressorSupport,
    portPressureRatio,
    portKpa: manifoldKpa * portPressureRatio,
  };
}

// Rough induction volume when the user has not measured it: plenum plus
// runners, plus the charge pipe and cooler volume on a boosted engine.
function estimateInductionVolume(spec, geo) {
  const runnerLitres =
    (spec.runnerLength * spec.runnerArea * spec.cylinders * 16.387) / 1000;
  const plenumLitres = (geo.Vd * spec.cylinders * 1000) * 1.2;
  const chargePipeLitres = spec.induction === "na" ? 0 : 6.0;
  return runnerLitres + plenumLitres + chargePipeLitres;
}

/* ================================================================== *
 *  CYLINDER FILLING  (mass balance at intake valve closing)
 *
 *  Replaces the older approach of multiplying a geometric trapping
 *  ratio by a separate residual factor. Those two are not independent:
 *  the cylinder contains residual AND fresh charge sharing one volume
 *  at one pressure, so the only correct way to split them is a mass
 *  and energy balance.
 *
 *  1. Burned gas is trapped at exhaust valve closing, at exhaust
 *     pressure and temperature.
 *  2. The intake stroke draws fresh charge in until IVC. The effective
 *     volume at IVC depends on the cam (late closing loses charge at
 *     low speed, packs it at high speed).
 *  3. At IVC the cylinder is at manifold pressure and a mixed
 *     temperature. Solving that balance gives the fresh mass trapped.
 *
 *  Hot residual is the dominant low-speed loss: it occupies several
 *  times the volume per unit mass that fresh charge does, so a modest
 *  residual MASS fraction denies a large share of the swept volume.
 * ================================================================== */

function calculateCylinderFilling(
  spec, geo, cam, rpm, manifoldKpa, exhaustKpa, machIndexRaw, portKpa, residualPurge, exhaustTempK
) {
  // Charge enters at PORT pressure, but VE is referenced to plenum
  // (MAP) density, because that is what the ECU measures.
  const Pport = (portKpa != null ? portKpa : manifoldKpa) * KPA_TO_PA;
  const Pman = manifoldKpa * KPA_TO_PA;
  const Pexh = exhaustKpa * KPA_TO_PA;
  const Tman = spec.chargeTempK;
  const Texh = exhaustTempK != null ? exhaustTempK : spec.exhaustTempK;

  // ---- 1. residual trapped at exhaust valve closing ----
  // EVC is quoted after TDC, so the cylinder has already started down.
  const vAtEVC = cylinderVolume(cam.evc, geo);
  // Overlap blow-through sweeps part of the residual out before the
  // intake stroke, so less of it remains to displace fresh charge.
  const residualMass =
    ((Pexh * vAtEVC) / (R_AIR * Texh)) * (1 - Math.min(0.95, residualPurge || 0));

  // ---- 2. effective volume at intake valve closing ----
  const vAtIVC = cylinderVolume(180 + cam.ivc, geo);
  const vAtBDC = cylinderVolume(180, geo);
  const trappingRatio = vAtIVC / vAtBDC;

  const x = machIndexRaw / COEFF.INERTIA_RECOVERY_Z;
  const recovery =
    Math.pow(x, COEFF.INERTIA_RECOVERY_ORDER) /
    (1 + Math.pow(x, COEFF.INERTIA_RECOVERY_ORDER));

  const volumeRatio =
    trappingRatio + (1 - trappingRatio) * recovery * COEFF.CAM_RAM_PACKING;
  const vEffective = volumeRatio * vAtBDC;

  // ---- 3. mass balance at IVC ----
  // Total mass is set by Pman, the effective volume and the mixed
  // temperature; the mixed temperature depends on how much fresh
  // charge came in. Converges in a handful of passes.
  let Tmix = Tman * 1.25;
  let totalMass = 0;
  let freshMass = 0;
  for (let i = 0; i < 12; i++) {
    totalMass = (Pport * vEffective) / (R_AIR * Tmix);
    freshMass = Math.max(0, totalMass - residualMass);
    const denom = freshMass + residualMass;
    const newT = denom > 0 ? (freshMass * Tman + residualMass * Texh) / denom : Tmix;
    if (Math.abs(newT - Tmix) < 0.5) { Tmix = newT; break; }
    Tmix = 0.5 * Tmix + 0.5 * newT;
  }

  const referenceMass = geo.Vd * (Pman / (R_AIR * Tman));
  const ve = (freshMass / referenceMass) * 100;

  const residualFraction =
    freshMass + residualMass > 0 ? residualMass / (freshMass + residualMass) : 1;

  return {
    ve, trappingRatio, recovery, volumeRatio,
    residualMass, freshMass, residualFraction, Tmix,
  };
}

/* ================================================================== *
 *  OVERLAP MASS TRANSFER
 *  Integrates the actual valve lift profiles across the overlap window
 *  to get a real effective flow area, then computes the mass that
 *  crosses the valves and what it does to cylinder filling.
 *
 *  Adverse pressure ratio (exhaust above manifold) pushes hot exhaust
 *  up the intake port. It is re-ingested on the following intake
 *  stroke, and because it is hot it denies far more cylinder VOLUME
 *  than its mass fraction suggests. This is the dominant loss at low
 *  engine speed on a long-overlap cam, where the overlap window lasts
 *  many milliseconds.
 *
 *  Favourable pressure ratio (manifold above exhaust) blows fresh
 *  charge through, sweeping residual out and raising trapped mass.
 * ================================================================== */

function meanOverlapArea(spec, cam) {
  // Sample both lobes across the overlap window and take the mean of
  // the SMALLER curtain area at each step: the two valves are in
  // series, so the tighter one governs.
  const iExp = lobeExponent(spec.intakeDuration050, cam.advI, spec.intakeValveLift);
  const eExp = lobeExponent(spec.exhaustDuration050, cam.advE, spec.exhaustValveLift);

  const steps = 24;
  let sum = 0;
  for (let k = 0; k < steps; k++) {
    // Crank angle through the overlap window, TDC at 0.
    const th = -cam.ivo + ((cam.ivo + cam.evc) * (k + 0.5)) / steps;

    const li = liftAt(th, spec.intakeCenterline, cam.advI, iExp, spec.intakeValveLift);
    const le = liftAt(th, 720 - spec.exhaustCenterline, cam.advE, eExp, spec.exhaustValveLift);

    const ai = spec.intakeValveCount * Math.PI * spec.intakeValveDiameter * li;
    const ae = spec.exhaustValveCount * Math.PI * spec.exhaustValveDiameter * le;

    sum += Math.min(ai, ae);
  }
  const meanIn2 = sum / steps;
  return meanIn2 * IN_TO_M * IN_TO_M; // m^2
}

function calculateOverlapTransfer(spec, geo, cam, rpm, manifoldKpa, exhaustKpa, exhaustTempK) {
  const overlapDeg = cam.overlap;
  if (overlapDeg <= 0) {
    return { massFraction: 0, dilution: 0, purge: 0, blowThrough: 0, factor: 1, area: 0, overlapSeconds: 0 };
  }

  const area = meanOverlapArea(spec, cam);
  const overlapSeconds = overlapDeg / (6 * rpm);

  const Tman = spec.chargeTempK;
  const Texh =
    (exhaustTempK != null ? exhaustTempK : spec.exhaustTempK) * COEFF.OVERLAP_EGT_FRACTION;

  const dP = Math.abs(exhaustKpa - manifoldKpa) * KPA_TO_PA;
  const adverse = exhaustKpa > manifoldKpa;

  const Tup = adverse ? Texh : Tman;
  const Pup = (adverse ? exhaustKpa : manifoldKpa) * KPA_TO_PA;
  const rhoUp = Pup / (R_AIR * Tup);

  const sonic = Math.sqrt(1.4 * R_AIR * Tup);
  const velocity = Math.min(
    COEFF.OVERLAP_VELOCITY_LIMIT * sonic,
    Math.sqrt((2 * dP) / rhoUp)
  );

  const transferredMass =
    COEFF.OVERLAP_CD * area * rhoUp * velocity * overlapSeconds;

  // Reference: the fresh charge the cylinder would hold at manifold
  // density if nothing interfered.
  const referenceMass = geo.Vd * (manifoldKpa * KPA_TO_PA) / (R_AIR * Tman);
  const massFraction = transferredMass / referenceMass;

  if (adverse) {
    // Hot exhaust denies volume in proportion to its density deficit.
    const thermalExpansion = Math.min(COEFF.REVERSION_THERMAL_CAP, Texh / Tman);
    const dilution = Math.min(
      0.80,
      massFraction * thermalExpansion * COEFF.REBREATHE_FRACTION
    );
    return {
      massFraction, dilution, purge: 0, blowThrough: 0,
      factor: 1 - dilution, area, overlapSeconds,
    };
  }

  // Blow-through. Fresh charge crosses the cylinder during overlap and
  // leaves out the exhaust. Two separate consequences:
  //
  //   1. It sweeps residual out, so MORE charge is trapped. That is a
  //      scavenging benefit and it acts on trapped VE.
  //   2. The blow-through air itself never stays in the cylinder, but
  //      it DOES pass through the engine, the wideband sees it, and it
  //      must be fuelled. It therefore belongs in delivery VE.
  //
  // Not capped: on a positive-displacement blower with exhaust near
  // atmospheric, blow-through is genuinely large and is the reason
  // real tables sit well above 100%.
  const purge = Math.min(0.95, massFraction * COEFF.SCAVENGE_EFFECTIVENESS);
  return {
    massFraction, dilution: 0, purge,
    blowThrough: massFraction,
    factor: 1, area, overlapSeconds,
  };
}

/* ================================================================== *
 *  EXHAUST PRESSURE MODEL
 * ================================================================== */

function calculateExhaustPressure(spec, geo, rpm, manifoldKpa) {
  if (spec.induction === "na") {
    // Header + exhaust system loss, rises with the square of flow.
    const flowRatio = (rpm * manifoldKpa) / (spec.maxRpm * spec.baroKpa);
    return spec.baroKpa * (1 + 0.10 * flowRatio * flowRatio);
  }

  if (spec.induction === "supercharged") {
    // Positive-displacement or centrifugal: no turbine in the exhaust.
    const flowRatio = (rpm * manifoldKpa) / (spec.maxRpm * spec.baroKpa);
    return spec.baroKpa * (1 + 0.14 * flowRatio * flowRatio);
  }

  // Turbocharged. Once the turbine nozzle is choked its pressure drop
  // rises roughly LINEARLY with corrected mass flow, not with its
  // square. The anchor is the engine's own maximum flow condition
  // (max boost at max rpm), so no operating point can extrapolate far
  // beyond where the user's stated backpressure ratio was quoted.
  const boostKpa = Math.max(0, manifoldKpa - spec.baroKpa);
  const maxManifoldKpa = spec.baroKpa + Math.max(1, spec.maxBoostPsi * 6.89476);

  const flowNow = manifoldKpa * rpm;
  const flowMax = maxManifoldKpa * spec.maxRpm;
  const flowRatio = Math.min(1.15, flowNow / flowMax);

  // backpressureRatio is quoted the way tuners measure it: exhaust
  // manifold gauge pressure divided by boost gauge pressure, at the
  // engine's peak flow point.
  const dpMax = spec.backpressureRatio * (maxManifoldKpa - spec.baroKpa);

  return spec.baroKpa + dpMax * Math.pow(flowRatio, COEFF.TURBINE_FLOW_EXPONENT);
}

function calculateVE(spec, rpm, manifoldKpa) {
  const geo = deriveGeometry(spec);
  const cam = deriveCamEvents(spec);

  const exhaustKpa = calculateExhaustPressure(spec, geo, rpm, manifoldKpa);

  const runner = calculateRunnerTuning(spec, geo, rpm);
  const base = calculateBaseAirflow(spec, geo, rpm, manifoldKpa, spec.chargeTempK);
  const valve = calculateValveFlowLimit(spec, geo, rpm);
  // Induction charging is computed and reported for inspection, but is
  // NOT applied to the filling calculation. See note in
  // calculateInductionCharging.
  const induction = calculateInductionCharging(spec, geo, rpm, manifoldKpa);
  const exhaustTempK = calculateExhaustTemperature(spec, rpm, manifoldKpa);
  const ovl = calculateOverlapTransfer(
    spec, geo, cam, rpm, manifoldKpa, exhaustKpa, exhaustTempK
  );

  const fill = calculateCylinderFilling(
    spec, geo, cam, rpm, manifoldKpa, exhaustKpa, valve.machIndexRaw,
    manifoldKpa, ovl.purge, exhaustTempK
  );
  const camEff = calculateCamEfficiency(spec, geo, cam, rpm, valve.machIndexRaw);
  const rpmEff = calculateRPMEfficiency(spec, rpm);
  const mapEff = calculateMAPEfficiency(spec, geo, cam, manifoldKpa, exhaustKpa);
  const exh = calculateExhaustBackpressureCorrection(
    spec, cam, rpm, manifoldKpa, exhaustKpa, runner.tunedRpm
  );

  // TRAPPED VE: the charge actually held in the cylinder at IVC. This
  // is the thermodynamic quantity, and it is what sets torque.
  const trappedVE =
    fill.ve *
    valve.factor *
    runner.factor *
    rpmEff.factor *
    exh.factor *
    (1 - ovl.dilution);

  // DELIVERY VE: every bit of air that passes THROUGH the engine,
  // trapped charge plus overlap blow-through. This is what a Holley VE
  // table has to represent, because the fuel is metered against total
  // airflow and the wideband measures the result. On a blown engine
  // with manifold pressure above exhaust, blow-through is what puts a
  // real table at 120-130%.
  const ve = trappedVE + ovl.blowThrough * 100;

  const veClamped = Math.max(1, Math.min(200, ve));

  // Predicted actual airflow vs theoretical
  const actualCFM = base.theoreticalCFM * (veClamped / 100) * (manifoldKpa / spec.baroKpa);

  return {
    ve: veClamped,
    trappedVE,
    blowThrough: ovl.blowThrough * 100,
    exhaustKpa,
    exhaustTempK,
    factors: {
      cylinderFilling: fill.ve / 100,
      inductionCharging: induction.portPressureRatio,
      valveFlowLimit: valve.factor,
      camTiming: camEff.factor,
      runnerTuning: runner.factor,
      rpmEfficiency: rpmEff.factor,
      mapFilling: mapEff.factor,
      exhaustScavenging: exh.factor,
      overlapTransfer: ovl.dilution > 0 ? 1 - ovl.dilution : 1,
    },
    detail: { base, valve, induction, fill, camEff, runner, rpmEff, mapEff, exh, ovl, cam, geo },
    theoreticalCFM: base.theoreticalCFM,
    actualCFM,
  };
}

/* ================================================================== *
 *  TABLE BUILDER
 * ================================================================== */

function generateVETable(spec, rpmAxis, mapAxis) {
  return mapAxis.map((kpa) => rpmAxis.map((rpm) => calculateVE(spec, rpm, kpa)));
}

/* ==================================================================== *
 *  UI LAYER — vanilla DOM, no framework.
 *  All VE maths lives above this line.
 * ==================================================================== */

(function () {
  "use strict";

  var PRESETS = {
    coyoteTurbo: { label: "Coyote 5.0 — turbo race", spec: {
      displacement:302, cylinders:8, bore:3.63, stroke:3.649, rodLength:5.933,
      compressionRatio:11.0, induction:"turbocharged", maxRpm:8000, peakTorqueRpm:4800, peakPowerRpm:7200,
      intakeValveDiameter:1.457, exhaustValveDiameter:1.220, intakeValveCount:2, exhaustValveCount:2,
      intakeDuration050:244, exhaustDuration050:242, intakeDurationAdv:296, exhaustDurationAdv:294,
      intakeValveLift:0.512, exhaustValveLift:0.512, intakeCenterline:106, exhaustCenterline:117,
      manifoldType:"individual", runnerLength:11, runnerArea:2.4, throttleBodyDiameter:4.0,
      maxBoostPsi:30, boostOnsetRpm:4500, backpressureRatio:1.2, camConfig:"fixed", camLayout:"dual" } },
    coyoteNA: { label: "Coyote 5.0 — stock cams, NA", spec: {
      displacement:302, cylinders:8, bore:3.63, stroke:3.649, rodLength:5.933,
      compressionRatio:11.0, induction:"na", maxRpm:7500, peakTorqueRpm:4250, peakPowerRpm:6500,
      intakeValveDiameter:1.457, exhaustValveDiameter:1.220, intakeValveCount:2, exhaustValveCount:2,
      intakeDuration050:211, exhaustDuration050:211, intakeDurationAdv:263, exhaustDurationAdv:263,
      intakeValveLift:0.472, exhaustValveLift:0.453, intakeCenterline:120, exhaustCenterline:120,
      manifoldType:"individual", runnerLength:11, runnerArea:2.4, throttleBodyDiameter:3.0,
      maxBoostPsi:0, camConfig:"vct", camLayout:"dual" } },
    ls: { label: "LS 6.0 — single turbo", spec: {
      displacement:364, cylinders:8, bore:4.0, stroke:3.622, rodLength:6.098,
      compressionRatio:9.5, induction:"turbocharged", maxRpm:7000, peakTorqueRpm:4200, peakPowerRpm:6200,
      intakeValveDiameter:2.02, exhaustValveDiameter:1.57, intakeValveCount:1, exhaustValveCount:1,
      intakeDuration050:224, exhaustDuration050:232, intakeDurationAdv:276, exhaustDurationAdv:284,
      intakeValveLift:0.570, exhaustValveLift:0.560, intakeCenterline:112, exhaustCenterline:118,
      manifoldType:"singlePlane", runnerLength:8, runnerArea:2.6, throttleBodyDiameter:4.0,
      maxBoostPsi:20, boostOnsetRpm:4000, backpressureRatio:1.4, camConfig:"fixed",
      camLayout:"single", lsa:115 } },
    sbfNA: { label: "Small block Ford 347 — NA", spec: {
      displacement:347, cylinders:8, bore:4.030, stroke:3.400, rodLength:5.400,
      compressionRatio:10.5, induction:"na", maxRpm:7000, peakTorqueRpm:4500, peakPowerRpm:6200,
      intakeValveDiameter:2.02, exhaustValveDiameter:1.60, intakeValveCount:1, exhaustValveCount:1,
      intakeDuration050:224, exhaustDuration050:232, intakeDurationAdv:274, exhaustDurationAdv:282,
      intakeValveLift:0.544, exhaustValveLift:0.555, intakeCenterline:108, exhaustCenterline:116,
      manifoldType:"dualPlane", runnerLength:9, runnerArea:2.1, throttleBodyDiameter:3.0,
      maxBoostPsi:0, camConfig:"fixed", camLayout:"single", lsa:112 } }
  };

  var MANIFOLDS = [
    ["individual","Individual runner / ITB"], ["longRunner","Long-runner port injected"],
    ["sheetMetal","Sheet metal / fabricated"], ["dualPlane","Dual plane"],
    ["singlePlane","Single plane"], ["plenum","Short-runner plenum / blower"]
  ];

  /* ------------------------------------------------------------------
   *  FUEL SCALING
   *
   *  The engine above computes TRUE volumetric efficiency. A Holley VE
   *  table is only equal to that when the ECU Fuel Type matches what is
   *  in the tank. The ECU meters fuel as  air mass / (stoich x lambda)
   *  using the stoich of its Fuel Type setting, so when the tank fuel
   *  needs more (or less) fuel per unit of air, the VE table has to
   *  carry that difference:
   *
   *      table VE = true VE x stoich(ECU fuel type) / stoich(tank fuel)
   *
   *  Gasoline scale with E98 in the tank: 14.7 / 9.0 = 1.63, which is
   *  why a tuned E98 table on the gas scale sits near 190% where the
   *  engine really breathes about 117%.
   * ------------------------------------------------------------------ */
  var ECU_SCALES = [
    ["gasoline", "Gasoline (14.7:1)", 14.7],
    ["e85",      "E85 (9.8:1)",       9.8],
    ["methanol", "Methanol (6.4:1)",  6.4]
  ];
  var FUELS = [
    ["gasoline", "Pump or race gasoline (14.7)", 14.7],
    ["e85",      "E85 (9.8)",                    9.8],
    ["e90",      "E90 (9.4)",                    9.4],
    ["e98",      "E98 / pure ethanol (9.0)",     9.0],
    ["methanol", "Methanol (6.4)",               6.4],
    ["custom",   "Custom — enter stoich AFR",    null]
  ];

  var DEFAULT_SPEC = {
    displacement:302, cylinders:8, bore:3.63, stroke:3.649, rodLength:5.933,
    compressionRatio:11.0, induction:"turbocharged", maxRpm:8000, peakTorqueRpm:4500, peakPowerRpm:7000,
    intakeValveDiameter:1.457, exhaustValveDiameter:1.22, intakeValveCount:2, exhaustValveCount:2,
    intakePortFlow:0, exhaustPortFlow:0, flowTestPressure:28,
    intakeDuration050:244, exhaustDuration050:242, intakeDurationAdv:296, exhaustDurationAdv:294,
    intakeValveLift:0.512, exhaustValveLift:0.512, intakeCenterline:106, exhaustCenterline:117,
    lsa:111.5, ivo:null, ivc:null, evo:null, evc:null, camConfig:"fixed", camLayout:"dual",
    manifoldType:"individual", runnerLength:11, runnerArea:2.4,
    throttleBodyDiameter:4.0, inductionVolume:0,
    primaryDiameter:1.75, primaryLength:32, collectorDiameter:3.0,
    maxBoostPsi:30, boostOnsetRpm:4500, backpressureRatio:1.16, intercoolerEff:0.75,
    baroKpa:101.3, iatF:120, exhaustTempK:1550
  };

  var state = {
    spec: JSON.parse(JSON.stringify(DEFAULT_SPEC)),
    rpmMin:700, rpmMax:9500, rpmCount:31,
    psiMin:-12, psiMax:60, mapCount:31, vacuumBias:0.6,
    // Fuel scaling is applied to the finished table, not to the physics,
    // so changing it never requires a regenerate.
    ecuFuel:"gasoline", actualFuel:"gasoline", customStoich:9.0,
    showPsi:true, debug:false, sel:null,
    // The table is only computed when Generate is pressed. Solving 961
    // cells on every keystroke made the page stutter and moved the grid
    // under the cursor while settings were still being entered.
    result:null, dirty:true,
    open:{ heads:false, cam:true, intake:false, exhaust:false, boost:true, env:false }
  };

  function esc(s){ return String(s).replace(/[&<>"]/g, function(c){
    return {"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;"}[c]; }); }

  function linspace(a,b,n){ var o=[],i; for(i=0;i<n;i++) o.push(a+(b-a)*i/(n-1)); return o; }
  function clampCount(n){ n=Math.round(n)||2; return Math.max(2, Math.min(31, n)); }

  function stoichOf(list, key){
    for (var i=0;i<list.length;i++) if (list[i][0]===key) return list[i][2];
    return null;
  }
  function labelOf(list, key){
    for (var i=0;i<list.length;i++) if (list[i][0]===key) return list[i][1];
    return key;
  }
  function ecuStoich(){ return stoichOf(ECU_SCALES, state.ecuFuel) || 14.7; }
  function tankStoich(){
    if (state.actualFuel === "custom") return state.customStoich > 0 ? state.customStoich : ecuStoich();
    return stoichOf(FUELS, state.actualFuel) || ecuStoich();
  }
  function fuelScale(){ return ecuStoich() / tankStoich(); }
  function fuelScaleIsUnity(){ return Math.abs(fuelScale() - 1) < 0.005; }

  function fuelScaleText(){
    var scale = fuelScale();
    if (fuelScaleIsUnity()) {
      return "ECU scale matches the fuel in the tank, so the table is true volumetric efficiency.";
    }
    return '<strong style="color:#e91414">Table values = true VE &times; ' + scale.toFixed(3) +
      '</strong> (' + ecuStoich().toFixed(1) + ' / ' + tankStoich().toFixed(1) +
      '). The ECU meters fuel for a ' + ecuStoich().toFixed(1) + ':1 stoich while the tank fuel needs ' +
      tankStoich().toFixed(1) + ':1, so the VE table has to carry the difference. ' +
      'That is correct for Holley &mdash; it is how a gasoline-scale E98 table ends up near 190%.';
  }

  // One cam in the block means the centrelines sit either side of the
  // lobe separation angle rather than being independent.
  function exhaustCenterlineFor(s){
    if (s.camLayout !== "single") return s.exhaustCenterline;
    return 2*s.lsa - s.intakeCenterline;
  }
  function engineSpec(s){
    var o = {}, k;
    for (k in s) o[k] = s[k];
    o.chargeTempK = (s.iatF-32)*5/9 + 273.15;
    o.exhaustCenterline = exhaustCenterlineFor(s);
    return o;
  }

  function rpmAxis(){ return linspace(state.rpmMin, state.rpmMax, clampCount(state.rpmCount)); }

  function mapAxis(){
    var n = clampCount(state.mapCount), psis;
    if (state.psiMin >= 0 || state.psiMax <= 0) {
      psis = linspace(state.psiMax, state.psiMin, n);
    } else {
      var nVac = Math.max(2, Math.min(n-2, Math.round(n*state.vacuumBias)));
      var nBoost = n - nVac;
      psis = linspace(state.psiMax, state.psiMax/nBoost, nBoost)
        .concat(linspace(0, state.psiMin, nVac));
    }
    return psis.map(function(p){ return p*6.89476 + state.spec.baroKpa; });
  }

  /* ------------------------------ markup ------------------------------ */

  function field(label, key, opts){
    opts = opts || {};
    var v = opts.value !== undefined ? opts.value : state.spec[key];
    return '<label class="bps-ve__field"><span class="bps-ve__fieldlabel">' + esc(label) +
      (opts.suffix ? " (" + esc(opts.suffix) + ")" : "") + '</span>' +
      '<input type="number" step="' + (opts.step||1) + '" value="' + v +
      '" data-spec="' + key + '"></label>';
  }
  function stateField(label, key, opts){
    opts = opts || {};
    return '<label class="bps-ve__field"><span class="bps-ve__fieldlabel">' + esc(label) +
      (opts.suffix ? " (" + esc(opts.suffix) + ")" : "") + '</span>' +
      '<input type="number" step="' + (opts.step||1) + '" value="' + state[key] +
      '" data-state="' + key + '"></label>';
  }
  function select(label, key, options){
    var html = '<label class="bps-ve__field"><span class="bps-ve__fieldlabel">' + esc(label) +
      '</span><select data-spec="' + key + '">';
    options.forEach(function(o){
      html += '<option value="' + esc(o[0]) + '"' +
        (state.spec[key] === o[0] ? " selected" : "") + '>' + esc(o[1]) + '</option>';
    });
    return html + '</select></label>';
  }
  // Dropdown bound to a top-level state key (not the engine spec).
  function stateSelect(label, key, options){
    var html = '<label class="bps-ve__field"><span class="bps-ve__fieldlabel">' + esc(label) +
      '</span><select data-fuel="' + key + '">';
    options.forEach(function(o){
      html += '<option value="' + esc(o[0]) + '"' +
        (state[key] === o[0] ? " selected" : "") + '>' + esc(o[1]) + '</option>';
    });
    return html + '</select></label>';
  }
  function panel(title, body, key, note){
    var coll = key !== undefined;
    var open = coll ? state.open[key] : true;
    return '<section class="bps-ve__panel' + (coll ? ' bps-ve__panel--collapsible' : '') +
      '" data-open="' + open + '"><h3' + (coll ? ' data-toggle="' + key + '"' : '') + '>' +
      '<span>' + esc(title) + '</span>' +
      (coll ? '<span class="bps-ve__chev">' + (open ? "–" : "+") + '</span>' : '') + '</h3>' +
      '<div class="bps-ve__body">' +
      (note ? '<p class="bps-ve__note">' + note + '</p>' : '') + body + '</div></section>';
  }

  function renderInputs(){
    var s = state.spec, h = "";

    h += panel("Engine",
      field("Displacement","displacement",{suffix:"ci"}) +
      field("Cylinders","cylinders") +
      field("Bore","bore",{suffix:"in",step:0.001}) +
      field("Stroke","stroke",{suffix:"in",step:0.001}) +
      field("Rod length","rodLength",{suffix:"in",step:0.001}) +
      field("Compression ratio","compressionRatio",{suffix:":1",step:0.1}) +
      select("Induction","induction",[["na","Naturally aspirated"],["turbocharged","Turbocharged"],["supercharged","Supercharged"]]) +
      field("Max rpm","maxRpm",{step:100}) +
      field("Expected peak torque rpm","peakTorqueRpm",{step:100}) +
      field("Expected peak power rpm","peakPowerRpm",{step:100}));

    h += panel("Cylinder heads",
      field("Intake valve diameter","intakeValveDiameter",{suffix:"in",step:0.001}) +
      field("Intake valves per cylinder","intakeValveCount") +
      field("Exhaust valve diameter","exhaustValveDiameter",{suffix:"in",step:0.001}) +
      field("Exhaust valves per cylinder","exhaustValveCount") +
      field("Intake port flow, peak","intakePortFlow",{suffix:"cfm"}) +
      field("Exhaust port flow, peak","exhaustPortFlow",{suffix:"cfm"}) +
      field("Flow test pressure","flowTestPressure",{suffix:"in H2O"}),
      "heads",
      "Port flow is optional. Supplied, it corrects the valve-area estimate; left at 0, capacity is estimated from valve geometry alone.");

    var camBody =
      field("Intake duration @ .050","intakeDuration050",{suffix:"&deg;"}) +
      field("Exhaust duration @ .050","exhaustDuration050",{suffix:"&deg;"}) +
      field("Intake advertised duration","intakeDurationAdv",{suffix:"&deg;"}) +
      field("Exhaust advertised duration","exhaustDurationAdv",{suffix:"&deg;"}) +
      field("Intake valve lift","intakeValveLift",{suffix:"in",step:0.001}) +
      field("Exhaust valve lift","exhaustValveLift",{suffix:"in",step:0.001}) +
      field("Intake centerline","intakeCenterline",{suffix:"&deg;"});

    if (s.camLayout === "single") {
      var ecl = exhaustCenterlineFor(s), adv = s.lsa - s.intakeCenterline;
      camBody += field("Lobe separation angle","lsa",{suffix:"&deg;",step:0.5}) +
        '<div class="bps-ve__derived" id="bpsve-ecl">Exhaust centerline ' + ecl.toFixed(1) +
        '&deg;, set by the single cam. ' +
        (Math.abs(adv) < 0.05 ? "Installed straight up, so both centerlines match the LSA."
          : "Cam is " + Math.abs(adv).toFixed(1) + "&deg; " + (adv > 0 ? "advanced" : "retarded") + ".") +
        '</div>';
    } else {
      camBody += field("Exhaust centerline","exhaustCenterline",{suffix:"&deg;"});
    }
    camBody += select("Cam layout","camLayout",[["single","Single cam in block"],["dual","Separate intake and exhaust cams"]]) +
      select("Cam configuration","camConfig",[["fixed","Fixed / locked"],["vct","VCT (phasers active)"]]);

    var cam = deriveCamEvents(engineSpec(s));
    camBody += '<div class="bps-ve__derived" id="bpsve-camderived">IVO ' + cam.ivo.toFixed(0) +
      '&deg; BTDC &middot; IVC ' + cam.ivc.toFixed(0) + '&deg; ABDC &middot; EVO ' +
      cam.evo.toFixed(0) + '&deg; BBDC &middot; EVC ' + cam.evc.toFixed(0) +
      '&deg; ATDC &middot; overlap ' + cam.overlap.toFixed(0) + '&deg;</div>';

    h += panel("Camshaft", camBody, "cam",
      "Advertised duration sets the true open window.");

    h += panel("Intake",
      select("Manifold type","manifoldType",MANIFOLDS) +
      field("Runner length","runnerLength",{suffix:"in",step:0.5}) +
      field("Runner cross-section","runnerArea",{suffix:"sq in",step:0.1}) +
      field("Throttle body diameter","throttleBodyDiameter",{suffix:"in",step:0.1}) +
      field("Induction volume, throttle to valve","inductionVolume",{suffix:"litres, 0 = estimate",step:0.5}),
      "intake");

    h += panel("Exhaust",
      field("Primary diameter","primaryDiameter",{suffix:"in",step:0.05}) +
      field("Primary length","primaryLength",{suffix:"in"}) +
      field("Collector diameter","collectorDiameter",{suffix:"in",step:0.1}),
      "exhaust");

    if (s.induction !== "na") {
      h += panel("Boost",
        field("Max boost","maxBoostPsi",{suffix:"psi"}) +
        field("Rpm at full boost","boostOnsetRpm",{step:100}) +
        (s.induction === "turbocharged"
          ? field("Backpressure ratio at peak flow","backpressureRatio",{step:0.05}) : "") +
        field("Intercooler effectiveness","intercoolerEff",{step:0.05}),
        "boost",
        "Backpressure ratio is exhaust manifold GAUGE pressure divided by boost GAUGE pressure at peak flow. Measured values on a well-matched single are near 1.1&ndash;1.2.");
    }

    h += panel("Environment",
      field("Barometric pressure","baroKpa",{suffix:"kPa",step:0.1}) +
      field("Manifold air temp","iatF",{suffix:"&deg;F"}) +
      field("Exhaust gas temp","exhaustTempK",{suffix:"K",step:25}),
      "env");

    document.getElementById("bpsve-inputs").innerHTML = h;
  }

  function fuelBody(){
    var b = '<div class="bps-ve__grid3">' +
      stateSelect("ECU Fuel Type (Holley scale)","ecuFuel",ECU_SCALES) +
      stateSelect("Fuel actually in the tank","actualFuel",FUELS) +
      (state.actualFuel === "custom"
        ? stateField("Stoich AFR of that fuel","customStoich",{step:0.1}) : "") +
      '</div>';
    b += '<div class="bps-ve__derived" id="bpsve-fuelscale">' + fuelScaleText() + '</div>';
    return b;
  }

  function axesBody(){
    var b = '<div class="bps-ve__grid3">' +
      stateField("Boost min","psiMin",{suffix:"psig"}) +
      stateField("Boost max","psiMax",{suffix:"psig"}) +
      stateField("Rows","mapCount") +
      stateField("RPM min","rpmMin",{step:50}) +
      stateField("RPM max","rpmMax",{step:50}) +
      stateField("Columns","rpmCount") + '</div>';

    if (state.psiMin < 0 && state.psiMax > 0) {
      b += '<label class="bps-ve__field"><span class="bps-ve__fieldlabel" id="bpsve-vaclabel">Vacuum resolution &mdash; ' +
        Math.round(clampCount(state.mapCount)*state.vacuumBias) + ' of ' + clampCount(state.mapCount) +
        ' rows below atmospheric</span>' +
        '<input type="range" min="0.2" max="0.85" step="0.05" value="' + state.vacuumBias +
        '" data-state="vacuumBias"></label>';
    }
    b += '<div class="bps-ve__inline">' +
      '<label class="bps-ve__check"><input type="checkbox" data-toggle-state="showPsi"' +
      (state.showPsi ? " checked" : "") + '> Show MAP as PSIG</label>' +
      '<label class="bps-ve__check"><input type="checkbox" data-toggle-state="debug"' +
      (state.debug ? " checked" : "") + '> Debug mode (click any cell)</label>' +
      '<span class="bps-ve__hint" id="bpsve-axhint">' + clampCount(state.mapCount) + ' &times; ' +
      clampCount(state.rpmCount) + '</span></div>';

    b += '<button class="bps-ve__btn bps-ve__generate" id="bpsve-generate">' +
      (state.result === null ? "Generate table"
        : (state.dirty ? "Regenerate — settings changed" : "Table is up to date")) +
      '</button>';
    b += '<span class="bps-ve__hint bps-ve__stale" id="bpsve-stale">' +
      (state.dirty && state.result ? "Settings have changed since this table was generated." : "") +
      '</span>';
    return b;
  }

  function generate(){
    var eng = engineSpec(state.spec);
    var rA = rpmAxis(), mA = mapAxis();
    state.result = { eng: eng, rA: rA, mA: mA, table: generateVETable(eng, rA, mA) };
    state.dirty = false;
    state.sel = null;
  }

  // Holley shows three significant figures in the grid: 99.9, then 104.
  // Matching that keeps the columns narrow enough to fit the page.
  function cellText(v){ return v >= 100 ? v.toFixed(0) : v.toFixed(1); }

  function buildTSV(table, scale){
    return table.map(function(row){
      return row.map(function(x){ return (x.ve * scale).toFixed(1); }).join("\t"); }).join("\n");
  }

  function renderOutput(){
    var h = "";

    // ---- fuel scaling ----
    h += panel("Fuel scaling", fuelBody(), undefined,
      "Holley meters fuel using the stoich of its <em>Fuel Type</em> setting. If that setting does not match " +
      "what is really in the tank, the VE table has to be scaled to make up the difference. " +
      "Pick both and the table below is scaled to paste straight in.");

    // ---- axes ----
    h += panel("Table axes", axesBody(), undefined,
      "Set the range and how many breakpoints your Holley table has; the axes are interpolated across it. Holley allows 31 per axis with Large Table checked, 16 without.");

    if (!state.result) {
      document.getElementById("bpsve-output").innerHTML = h;
      return;
    }

    var eng = state.result.eng, rA = state.result.rA, mA = state.result.mA;
    var table = state.result.table;
    var scale = fuelScale();

    var flat = [], r, c;
    for (r=0;r<table.length;r++) for (c=0;c<table[r].length;c++) flat.push(table[r][c].ve);
    var gMax = Math.max.apply(null, flat), gMin = Math.min.apply(null, flat);

    // ---- grid ----
    var wide = rA.length > 20 ? ' data-cols="wide"' : '';
    var t = '<div class="bps-ve__tablewrap"><table' + wide + '><thead><tr><th>' +
      (state.showPsi ? "PSIG" : "kPa") + '</th>';
    for (c=0;c<rA.length;c++) t += '<th>' + Math.round(rA[c]) + '</th>';
    t += '</tr></thead><tbody>';
    for (r=0;r<table.length;r++){
      var lab = state.showPsi
        ? ((mA[r]-state.spec.baroKpa)/6.89476).toFixed(1)
        : Math.round(mA[r]);
      t += '<tr><th>' + lab + '</th>';
      for (c=0;c<table[r].length;c++){
        var v = table[r][c].ve;
        var f = (v-gMin)/Math.max(1e-6,(gMax-gMin));
        var g = Math.round(255-232*f);
        var isSel = state.sel && state.sel.r===r && state.sel.c===c;
        t += '<td class="' + (isSel?'bps-ve__sel':'') + '" data-r="' + r + '" data-c="' + c +
          '" style="background:rgb(255,'+g+','+g+');color:' + (f>0.62?'#fff':'#121212') + '">' +
          cellText(v * scale) + '</td>';
      }
      t += '</tr>';
    }
    t += '</tbody></table></div>';

    var tsv = buildTSV(table, scale);

    t += '<div class="bps-ve__actions">' +
      '<button class="bps-ve__btn" id="bpsve-copy">Copy for Holley</button>' +
      '<button class="bps-ve__btn bps-ve__btn--alt" id="bpsve-csv">Download CSV</button>' +
      '<span class="bps-ve__hint">Values only, no headers. Click the top-left cell in Holley before pasting.</span>' +
      '</div><textarea id="bpsve-tsv" rows="2" readonly>' + esc(tsv) + '</textarea>';

    var tableTitle = "Volumetric efficiency table (%) — " + mA.length + " × " + rA.length;
    var tableNote = fuelScaleIsUnity() ? undefined
      : '<span id="bpsve-fuelbanner" style="color:#e91414;font-weight:600">' +
        labelOf(ECU_SCALES, state.ecuFuel).replace(/ \(.*\)$/, "") + ' scale, ' +
        (state.actualFuel === "custom" ? "custom fuel" : labelOf(FUELS, state.actualFuel).replace(/ \(.*\)$/, "")) +
        ' in the tank &mdash; every value is true VE &times; ' + scale.toFixed(3) + '.</span>';
    h += panel(tableTitle, t, undefined, tableNote);

    // ---- debug ----
    if (state.debug && state.sel){
      var cell = table[state.sel.r][state.sel.c], d = cell.detail;
      var rows = [
        ["Overlap blow-through", 1 + cell.blowThrough/100,
          cell.trappedVE.toFixed(1) + "% trapped, +" + cell.blowThrough.toFixed(1) + "% passing through"],
        ["Cylinder filling (mass balance)", cell.factors.cylinderFilling,
          "residual " + (d.fill.residualFraction*100).toFixed(0) + "% of trapped mass, effective volume " +
          (d.fill.volumeRatio*100).toFixed(0) + "% of BDC"],
        ["Valve / port flow limit", cell.factors.valveFlowLimit,
          "inlet Mach index " + d.valve.machIndex.toFixed(3)],
        ["Runner tuning", cell.factors.runnerTuning,
          "Helmholtz tuned " + d.runner.tunedRpm.toFixed(0) + " rpm"],
        ["Low-speed efficiency", cell.factors.rpmEfficiency, "heat transfer and mixture motion"],
        ["Exhaust / header tuning", cell.factors.exhaustScavenging,
          "Pexh " + cell.exhaustKpa.toFixed(0) + " kPa, pressure ratio " + d.exh.pressureRatio.toFixed(2) +
          ", EGT " + cell.exhaustTempK.toFixed(0) + " K"]
      ];
      var dh = '<div style="font-size:13px;margin-bottom:8px"><strong>' +
        Math.round(rA[state.sel.c]) + ' rpm</strong> &middot; <strong>' +
        Math.round(mA[state.sel.r]) + ' kPa</strong> (' +
        ((mA[state.sel.r]-state.spec.baroKpa)/6.89476).toFixed(1) + ' psig)</div>' +
        '<table class="bps-ve__factors"><tbody>';
      rows.forEach(function(x){
        var col = x[1] >= 1 ? "#1a8f3c" : (x[1] < 0.85 ? "#e91414" : "#121212");
        dh += '<tr><td>' + esc(x[0]) + '</td><td class="bps-ve__fv" style="color:' + col + '">' +
          x[1].toFixed(3) + '</td><td class="bps-ve__fn">' + esc(x[2]) + '</td></tr>';
      });
      dh += '<tr class="bps-ve__ftotal"><td>True volumetric efficiency</td><td class="bps-ve__fv">' +
        cell.ve.toFixed(1) + '%</td><td class="bps-ve__fn">' +
        cell.theoreticalCFM.toFixed(0) + ' cfm theoretical &rarr; ' +
        cell.actualCFM.toFixed(0) + ' cfm predicted</td></tr>';
      if (!fuelScaleIsUnity()) {
        dh += '<tr><td>Fuel scale</td><td class="bps-ve__fv" style="color:#e91414">' +
          scale.toFixed(3) + '</td><td class="bps-ve__fn">' +
          ecuStoich().toFixed(1) + ' ECU stoich / ' + tankStoich().toFixed(1) + ' tank fuel stoich</td></tr>' +
          '<tr class="bps-ve__ftotal"><td>Value in the table</td><td class="bps-ve__fv">' +
          (cell.ve * scale).toFixed(1) + '%</td><td class="bps-ve__fn">what Holley needs on this scale</td></tr>';
      }
      dh += '</tbody></table>';
      h += panel("Cell factor breakdown", dh);
    }

    // ---- summary ----
    var refKpa = state.spec.induction === "na"
      ? state.spec.baroKpa
      : Math.min(state.spec.baroKpa + state.spec.maxBoostPsi*6.89476, Math.max.apply(null,mA));
    var sweep = linspace(Math.max(800,rA[0]), Math.min(state.spec.maxRpm, rA[rA.length-1]), 40);
    var peak = null;
    sweep.forEach(function(n){
      var res = calculateVE(eng, n, refKpa);
      if (!peak || res.ve > peak.ve) { peak = res; peak.rpm = n; }
    });
    var runner = calculateRunnerTuning(eng, deriveGeometry(eng), peak.rpm);
    var camE = deriveCamEvents(eng);

    var tiles = [
      ["Predicted peak VE (true)", peak.ve.toFixed(1) + "%"]
    ];
    if (!fuelScaleIsUnity()) tiles.push(["Peak value on the ECU scale", (peak.ve * scale).toFixed(1) + "%"]);
    tiles = tiles.concat([
      ["Trapped VE at that point", peak.trappedVE.toFixed(1) + "%"],
      ["At engine speed", Math.round(peak.rpm) + " rpm"],
      ["Intake resonance tuned to", runner.tunedRpm.toFixed(0) + " rpm"],
      ["Calculated overlap", camE.overlap.toFixed(0) + "°"],
      ["Exhaust temp at peak", peak.exhaustTempK.toFixed(0) + " K"],
      ["Theoretical airflow at peak", peak.theoreticalCFM.toFixed(0) + " cfm"],
      ["Predicted actual airflow", peak.actualCFM.toFixed(0) + " cfm"]
    ]);
    var sh = '<div class="bps-ve__tiles">';
    tiles.forEach(function(x){
      sh += '<div class="bps-ve__tile"><div class="bps-ve__tilek">' + esc(x[0]) +
        '</div><div class="bps-ve__tilev">' + esc(x[1]) + '</div></div>';
    });
    sh += '</div><p class="bps-ve__closing">Figures shown at ' + refKpa.toFixed(0) +
      ' kPa manifold pressure. This is a calculated starting point, not a finished calibration &mdash; ' +
      'datalog it and correct from measured lambda. Expect the 1500&ndash;3500 rpm region to need the most work.</p>';
    h += panel("Calculation details", sh);

    document.getElementById("bpsve-output").innerHTML = h;
  }

  // Re-apply the fuel scale to an already-rendered grid without
  // rebuilding it, so typing a custom stoich value keeps focus.
  function rescaleGrid(){
    if (!state.result) return;
    var table = state.result.table, scale = fuelScale();
    var tds = document.querySelectorAll("#bpsve-output td[data-r]");
    for (var i=0;i<tds.length;i++){
      var r = parseInt(tds[i].dataset.r,10), c = parseInt(tds[i].dataset.c,10);
      tds[i].textContent = cellText(table[r][c].ve * scale);
    }
    var ta = document.getElementById("bpsve-tsv");
    if (ta) ta.value = buildTSV(table, scale);
    var banner = document.getElementById("bpsve-fuelbanner");
    if (banner) banner.innerHTML = labelOf(ECU_SCALES, state.ecuFuel).replace(/ \(.*\)$/, "") +
      ' scale, ' + (state.actualFuel === "custom" ? "custom fuel" : labelOf(FUELS, state.actualFuel).replace(/ \(.*\)$/, "")) +
      ' in the tank &mdash; every value is true VE &times; ' + scale.toFixed(3) + '.';
  }

  function renderPresets(){
    var el = document.getElementById("bpsve-presets");
    var h = '<span class="bps-ve__presetlabel">Start from</span>';
    Object.keys(PRESETS).forEach(function(k){
      h += '<button class="bps-ve__preset" data-preset="' + k + '">' + esc(PRESETS[k].label) + '</button>';
    });
    h += '<button class="bps-ve__reset" data-preset="__reset">Reset</button>';
    el.innerHTML = h;
  }

  function renderAll(){ renderInputs(); renderOutput(); }

  // Typing must not rebuild the inputs. Re-rendering that panel replaces
  // the very <input> being typed into, which drops focus and swallows
  // keystrokes — that is what made multi-digit entry impossible. Only
  // the small derived readouts are refreshed while a value changes.
  function setText(id, html){
    var el = document.getElementById(id);
    if (el) el.innerHTML = html;
  }

  function refreshLive(){
    var s = state.spec;

    var btn = document.getElementById("bpsve-generate");
    if (btn) btn.textContent = state.result === null ? "Generate table"
      : (state.dirty ? "Regenerate — settings changed" : "Table is up to date");
    setText("bpsve-stale", state.dirty && state.result
      ? "Settings have changed since this table was generated." : "");

    setText("bpsve-axhint", clampCount(state.mapCount) + " &times; " + clampCount(state.rpmCount));
    if (state.psiMin < 0 && state.psiMax > 0) {
      setText("bpsve-vaclabel", "Vacuum resolution &mdash; " +
        Math.round(clampCount(state.mapCount)*state.vacuumBias) + " of " +
        clampCount(state.mapCount) + " rows below atmospheric");
    }

    setText("bpsve-fuelscale", fuelScaleText());

    try {
      var cam = deriveCamEvents(engineSpec(s));
      setText("bpsve-camderived", "IVO " + cam.ivo.toFixed(0) +
        "&deg; BTDC &middot; IVC " + cam.ivc.toFixed(0) + "&deg; ABDC &middot; EVO " +
        cam.evo.toFixed(0) + "&deg; BBDC &middot; EVC " + cam.evc.toFixed(0) +
        "&deg; ATDC &middot; overlap " + cam.overlap.toFixed(0) + "&deg;");
      if (s.camLayout === "single") {
        var ecl = exhaustCenterlineFor(s), adv = s.lsa - s.intakeCenterline;
        setText("bpsve-ecl", "Exhaust centerline " + ecl.toFixed(1) +
          "&deg;, set by the single cam. " +
          (Math.abs(adv) < 0.05 ? "Installed straight up, so both centerlines match the LSA."
            : "Cam is " + Math.abs(adv).toFixed(1) + "&deg; " + (adv > 0 ? "advanced" : "retarded") + "."));
      }
    } catch (e) { /* mid-edit values can be incomplete; ignore until valid */ }
  }

  // Focus stays put while typing: re-rendering the inputs on every
  // keystroke would blur the field being edited, so only the panel
  // structure is redrawn when a value actually changes the layout.

  /* --------------------------- events --------------------------- */

  function onInput(e){
    var t = e.target, k;
    if (t.dataset.fuel !== undefined) {
      // Fuel scaling is a post-scale on the finished table: no
      // regenerate needed, and no dirty flag.
      state[t.dataset.fuel] = t.value;
      renderOutput();
      return;
    }
    if (t.dataset.spec !== undefined) {
      k = t.dataset.spec;
      if (t.tagName === "SELECT") {
        // A select can change which fields exist (cam layout, induction),
        // so the panel does have to be rebuilt — but focus is not an
        // issue because the value is already committed.
        state.spec[k] = t.value;
        state.dirty = true;
        renderAll();
        return;
      }
      var n = parseFloat(t.value);
      state.spec[k] = isNaN(n) ? "" : n;
      state.dirty = true;
      refreshLive();
    } else if (t.dataset.state !== undefined) {
      k = t.dataset.state;
      var m = parseFloat(t.value);
      if (!isNaN(m)) state[k] = m;
      if (k === "customStoich") {
        // Changes the output scale only, never the physics.
        refreshLive();
        rescaleGrid();
        return;
      }
      state.dirty = true;
      refreshLive();
    } else if (t.dataset.toggleState !== undefined) {
      state[t.dataset.toggleState] = t.checked;
      if (t.dataset.toggleState === "debug") state.sel = null;
      renderOutput();
    }
  }

  function onClick(e){
    var t = e.target;
    var toggle = t.closest ? t.closest("[data-toggle]") : null;
    if (toggle) { var k = toggle.dataset.toggle; state.open[k] = !state.open[k]; renderInputs(); return; }

    var preset = t.closest ? t.closest("[data-preset]") : null;
    if (preset) {
      var p = preset.dataset.preset;
      if (p === "__reset") state.spec = JSON.parse(JSON.stringify(DEFAULT_SPEC));
      else { var src = PRESETS[p].spec, kk; for (kk in src) state.spec[kk] = src[kk]; }
      state.sel = null; state.dirty = true; renderAll(); return;
    }

    if (t.id === "bpsve-generate") {
      generate();
      renderOutput();
      return;
    }
    if (t.id === "bpsve-copy") {
      var ta = document.getElementById("bpsve-tsv");
      ta.select(); ta.setSelectionRange(0, 999999);
      try { document.execCommand("copy"); t.textContent = "Copied";
        setTimeout(function(){ t.textContent = "Copy for Holley"; }, 1500); } catch (err) {}
      return;
    }
    if (t.id === "bpsve-csv") {
      var txt = document.getElementById("bpsve-tsv").value.replace(/\t/g, ",");
      var blob = new Blob([txt], {type:"text/csv"});
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url; a.download = "ve-table.csv"; a.click();
      URL.revokeObjectURL(url);
      return;
    }
    if (state.debug && t.dataset && t.dataset.r !== undefined) {
      state.sel = { r: parseInt(t.dataset.r,10), c: parseInt(t.dataset.c,10) };
      renderOutput();
    }
  }

  function init(){
    var root = document.querySelector(".bps-ve");
    if (!root) return;
    renderPresets();
    renderAll();
    root.addEventListener("input", onInput);
    root.addEventListener("change", onInput);
    root.addEventListener("click", onClick);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();